/**
 * Lightweight fetch wrapper for the Adonis Agency API.
 *
 * - Always sends cookies (httpOnly JWT)
 * - Adds JSON Content-Type when a JSON body is provided
 * - Throws ApiError with `status` and human message for non-2xx
 *
 * Keep this file dependency-free (no React, no axios) so it can be reused
 * in any context.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const buildUrl = (path) => {
  if (!path.startsWith("/")) {
    throw new Error(`api path must start with '/': got "${path}"`);
  }
  return `${API_BASE}${path}`;
};

const parseDetail = async (res) => {
  try {
    const data = await res.json();
    if (typeof data.detail === "string") return { detail: data.detail, body: data };
    return { detail: undefined, body: data };
  } catch {
    return { detail: undefined, body: undefined };
  }
};

export async function apiRequest(
  path,
  { method = "GET", body, headers = {}, signal } = {},
) {
  const init = {
    method,
    credentials: "include",
    headers: { ...headers },
    signal,
  };
  if (body !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(buildUrl(path), init);
  if (!res.ok) {
    const { detail, body: errBody } = await parseDetail(res);
    throw new ApiError(detail || res.statusText || "Request failed", {
      status: res.status,
      body: errBody,
    });
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const apiGet = (path, opts) => apiRequest(path, { ...opts, method: "GET" });
export const apiPost = (path, body, opts) =>
  apiRequest(path, { ...opts, method: "POST", body });
